import axios from "axios";
import { useParams } from "react-router-dom";
import React, { useState, useEffect, useMemo, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserState } from "../../../app/selector";

import "bootstrap/dist/css/bootstrap.min.css";
import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import {
  BasicStorage,
  ChatProvider,
  Conversation,
  ConversationRole,
  Participant,
  Presence,
  TypingUsersList,
  User,
  UserStatus,
  ChatMessage,
  MessageContentType,
  MessageDirection,
  MessageStatus,
} from "@chatscope/use-chat";
import { ChatService } from "./services/ChatService";
import { Chat } from "./Chat";
import { nanoid } from "nanoid";
import { AutoDraft } from "@chatscope/use-chat/dist/enums/AutoDraft";

import { WebSocketContext } from "../../../context";
import { BACKEND_URL } from "constants";

const messageIdGenerator = (message) => nanoid();
const groupIdGenerator = () => nanoid();

function createConversation(id, name) {
  return new Conversation({
    id,
    participants: [
      new Participant({
        id: name,
        role: new ConversationRole([]),
      }),
    ],
    unreadCounter: 0,
    typingUsers: new TypingUsersList({ items: [] }),
    draft: "",
  });
}

function Inbox() {
  const dispatch = useDispatch();
  const { active_participant_id } = useParams();
  const socket = useContext(WebSocketContext);

  const defaultUserStorage = useMemo(
    () => new BasicStorage({ groupIdGenerator, messageIdGenerator }),
    []
  );

  // Create serviceFactory
  const serviceFactory = (storage, updateState) => {
    return new ChatService(storage, updateState, socket);
  };

  // selectors
  const [userStorage, setUserStorage] = useState(defaultUserStorage);
  const [thumbnail, setThumbnail] = useState(null);
  const [participantConversationMap, setParticipantConversationMap] = useState(
    {}
  );
  const { user } = useSelector(getUserState);
  const [participants, setParticipants] = useState(null);
  const [messages, setMessages] = useState(null);

  const chatUser = new User({
    id: user?.user_id,
    presence: new Presence({ status: UserStatus.Available, description: "" }),
    username: user?.username,
    avatar: thumbnail,
  });

  // Handle disconnection when component unmounts
  useEffect(() => {
    return () => {
      if (socket) {
        socket.emit("disconnect_chat", {
          user_id: user?.user_id,
          date: Date.now(),
        });
      }
    };
  }, [user, socket]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!participants) {
          const participantsResponse = await axios.post(
            `${BACKEND_URL}/get-participants`
          );
          const users = participantsResponse.data;
          const updatedParticipants = {};
          users.forEach((user) => {
            updatedParticipants[user["user_id"]] = user;
          });
          setParticipants(updatedParticipants);
        }
        if (!messages) {
          const messagesResponse = await axios.post(
            `${BACKEND_URL}/get-messages`
          );
          setMessages(messagesResponse.data);
        }
        const photo = await axios.post(
          `${BACKEND_URL}/photos/by_user_id:main_photo`,
          {}
        );
        if (photo?.data) {
          setThumbnail({ thumbnail: `data:image/jpeg;base64,${photo.data["thumbnail"]}`, "photo_id": photo.data["photo_id"] });
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [dispatch]);

  const readMessage = (sender_user_id) => {
    axios
      .post(`${BACKEND_URL}/read-messages`, { sender_user_id })
      .catch((e) => console.error(e));
  };

  // Handle real-time messages
  /* useEffect(() => {
    if (!socket) return;

    socket.on("receiveMessage", (message) => {
      console.log("New message received:", message);

      const sender = message.sender_user_id;
      const receiver = message.receiver_user_id;
      const direction =
        sender === user.user_id
          ? MessageDirection.Outgoing
          : MessageDirection.Incoming;
      const timestamp = message.timestamp;
      const conversationId =
        direction === MessageDirection.Outgoing
          ? participantConversationMap[receiver]
          : participantConversationMap[sender];

      const msg = new ChatMessage({
        id: "",
        content: message.content,
        contentType: MessageContentType.TextHtml,
        senderId: sender,
        direction: direction,
        status: MessageStatus.Sent,
        createdTime: new Date(timestamp),
      });

      userStorage.addMessage(msg, conversationId, true);
      setUserStorage(userStorage);
    });

    return () => {
      socket.off("receiveMessage");
    };
  }, [socket, participantConversationMap, user, userStorage]); */

  useEffect(() => {
    if (!participants) return;
    for (const userId in participants) {
      if (userId !== user.user_id) {
        const otherUser = participants[userId];
        const otherChatUser = new User({
          id: otherUser.user_id,
          presence: new Presence({
            status: UserStatus.Available,
            description: "",
          }),
          firstName: "",
          lastName: "",
          username: otherUser.username,
          email: "",
          avatar: null,
          bio: "",
          data: {
            isAdmin: otherUser.privilege !== 0,
            isBlocked: otherUser.blocked,
            isBeingBlocked: otherUser.is_being_blocked,
          },
        });
        if (otherUser.thumbnail) {
          otherChatUser.avatar = {
            "thumbnail": `data:image/jpeg;base64,${otherUser.thumbnail}`,
            "photo_id": otherUser.photo_id
          }
        }

        defaultUserStorage.addUser(otherChatUser);
        const conversationId = nanoid();

        const existingConversation = defaultUserStorage
          .getState()
          .conversations.find((cv) =>
            cv.participants.some((p) => p.id === otherChatUser.id)
          );

        if (!existingConversation) {
          defaultUserStorage.addConversation(
            createConversation(conversationId, otherChatUser.id)
          );
        }
        participantConversationMap[otherChatUser.id] = conversationId;
        if (active_participant_id === userId) {
          defaultUserStorage.setActiveConversation(conversationId);
          readMessage(active_participant_id);
        }
      }
    }
    setUserStorage(defaultUserStorage);
    setParticipantConversationMap(participantConversationMap);
  }, [
    participants,
    participantConversationMap,
    user,
    defaultUserStorage,
    active_participant_id,
  ]);

  useEffect(() => {
    if (messages === null) return;
    if (Object.keys(participantConversationMap).length === 0) return;
    for (const message of messages) {
      const sender = message["sender_user_id"];
      const receiver = message["receiver_user_id"];
      const direction =
        sender === user.user_id
          ? MessageDirection.Outgoing
          : MessageDirection.Incoming;
      const timestamp = message["timestamp"];
      let conversationId = "";
      if (direction === MessageDirection.Outgoing) {
        conversationId = participantConversationMap[receiver];
      } else {
        conversationId = participantConversationMap[sender];
      }
      const msg = new ChatMessage({
        id: "", // Id will be generated by storage generator, so here you can pass an empty string
        content: message["content"],
        contentType: MessageContentType.TextHtml,
        senderId: sender,
        direction: direction,
        status: MessageStatus.Sent,
        createdTime: new Date(timestamp),
      });
      if (message.read === 0 && receiver === user.user_id) {
        const [conversation] = userStorage.getConversation(conversationId);
        conversation.unreadCounter = conversation.unreadCounter + 1;
      }
      userStorage.addMessage(msg, conversationId, true);
      setUserStorage(userStorage);
    }
  }, [messages, participantConversationMap, user, userStorage]);

  if (!userStorage) {
    return null;
  }
  window.userStorage = userStorage;

  return (
    <div className="content-block flex bg-light p-4 rounded inbox-container">
      <h1 className="text-center menu-title">Chat</h1>
      <ChatProvider
        serviceFactory={serviceFactory}
        storage={userStorage}
        config={{
          typingThrottleTime: 250,
          typingDebounceTime: 900,
          debounceTyping: true,
          autoDraft: AutoDraft.Save | AutoDraft.Restore,
        }}
      >
        <Chat user={chatUser} />
      </ChatProvider>
    </div>
  );
}

export default Inbox;
